import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import SectionTitle from "@/components/SectionTitle";
import { Send, CheckCircle2, Bot, RefreshCw, ShieldCheck } from "lucide-react";

export default function TelegramSection() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminTg, setAdminTg] = useState(null);

  const load = () => api.get("/telegram/status").then((r) => setStatus(r.data)).catch(() => {});
  const loadAdmin = () => api.get("/telegram/admin/status").then((r) => { setIsAdmin(true); setAdminTg(r.data); }).catch(() => setIsAdmin(false));
  useEffect(() => { load(); loadAdmin(); }, []);

  const act = async (fn, key, okMsg) => {
    setBusy(key);
    try {
      await fn();
      toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal. Coba lagi.");
    } finally {
      setBusy(null);
    }
  };

  if (!isAdmin) return null;

  return (
    <section id="telegram" className="scroll-mt-20">
      <SectionTitle
        eyebrow="Broadcast Otomatis 08:15 WIB"
        title="Alert Telegram Grup Member"
        desc="Hubungkan grup Telegram Anda agar briefing pagi + rekomendasi & scalping terkirim otomatis setiap hari."
        icon={Send}
      />
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#229ED9]/15 flex items-center justify-center shrink-0">
            <Bot size={22} className="text-[#4cb5e8]" />
          </div>
          <div className="flex-1">
            <p className="font-heading font-semibold">
              Bot: <span className="text-accent" data-testid="tg-bot-username">@{status?.bot_username || "..."}</span>
            </p>
            <p className="text-sm text-[var(--text-secondary)]">
              {status?.configured ? (
                <span className="inline-flex items-center gap-1.5 text-bull"><CheckCircle2 size={14} /> Grup terhubung (Chat ID: {status.chat_id})</span>
              ) : (
                "Belum terhubung ke grup mana pun."
              )}
            </p>
          </div>
          <button onClick={load} className="p-2 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-secondary)] hover:text-accent transition-colors" data-testid="tg-refresh-status">
            <RefreshCw size={16} />
          </button>
        </div>

        <div className="bg-[var(--surface-base)] rounded-lg p-4 border border-[var(--border-subtle)] mb-6">
          <p className="text-sm font-semibold mb-2">Cara menghubungkan:</p>
          <ol className="text-sm text-[var(--text-secondary)] space-y-1 list-decimal list-inside">
            <li>Tambahkan bot <span className="text-accent font-medium">@{status?.bot_username || "FasTradingInvestama_bot"}</span> ke grup Telegram Anda.</li>
            <li>Kirim satu pesan apa saja di grup tersebut.</li>
            <li>Klik <b>Deteksi Chat ID Grup</b> di bawah ini.</li>
          </ol>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => act(() => api.post("/telegram/detect"), "detect", "Chat ID grup berhasil dideteksi!")}
            disabled={busy === "detect"}
            data-testid="tg-detect-btn"
            className="h-10 px-4 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors"
          >
            {busy === "detect" ? "Mendeteksi..." : "Deteksi Chat ID Grup"}
          </button>
          <button
            onClick={() => act(() => api.post("/telegram/test"), "test", "Pesan tes terkirim ke grup!")}
            disabled={busy === "test" || !status?.configured}
            data-testid="tg-test-btn"
            className="h-10 px-4 rounded-lg bg-[var(--surface-hover)] border border-[var(--border)] font-semibold hover:bg-[var(--border)] disabled:opacity-40 transition-colors"
          >
            {busy === "test" ? "Mengirim..." : "Kirim Pesan Tes"}
          </button>
          <button
            onClick={() => act(() => api.post("/telegram/broadcast"), "bc", "Briefing hari ini terkirim ke grup!")}
            disabled={busy === "bc" || !status?.configured}
            data-testid="tg-broadcast-btn"
            className="h-10 px-4 rounded-lg bg-[var(--surface-hover)] border border-[var(--border)] font-semibold hover:bg-[var(--border)] disabled:opacity-40 transition-colors"
          >
            {busy === "bc" ? "Mengirim..." : "Kirim Briefing Sekarang"}
          </button>
          <button
            onClick={() => act(() => api.post("/telegram/broadcast-scalping"), "scalp", "Alert scalping terkirim ke grup!")}
            disabled={busy === "scalp" || !status?.configured}
            data-testid="tg-scalping-btn"
            className="h-10 px-4 rounded-lg bg-[var(--amber)]/15 border border-[var(--amber)]/30 text-amber font-semibold hover:bg-[var(--amber)]/25 disabled:opacity-40 transition-colors"
          >
            {busy === "scalp" ? "Memindai..." : "Kirim Alert Scalping"}
          </button>
          <button
            onClick={() => act(() => api.post("/telegram/broadcast-watchlist"), "wl", "Ringkasan watchlist terkirim ke grup!")}
            disabled={busy === "wl" || !status?.configured}
            data-testid="tg-watchlist-btn"
            className="h-10 px-4 rounded-lg bg-[var(--surface-hover)] border border-[var(--border)] font-semibold hover:bg-[var(--border)] disabled:opacity-40 transition-colors"
          >
            {busy === "wl" ? "Mengirim..." : "Kirim Ringkasan Watchlist"}
          </button>
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-4">Alert scalping otomatis dipindai & dikirim tiap 09:30, 11:30, 13:30, 15:30 WIB (hari bursa). Ringkasan watchlist otomatis tiap 16:15 WIB.</p>
      </div>

      {isAdmin && (
        <div className="card p-6 mt-6" data-testid="tg-admin-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-5">
            <div className="w-11 h-11 rounded-xl bg-[var(--accent)]/15 flex items-center justify-center shrink-0">
              <ShieldCheck size={22} className="text-accent" />
            </div>
            <div className="flex-1">
              <p className="font-heading font-semibold">Notifikasi Admin — Konfirmasi Transfer BRI</p>
              <p className="text-sm text-[var(--text-secondary)]">
                {adminTg?.configured ? (
                  <span className="inline-flex items-center gap-1.5 text-bull"><CheckCircle2 size={14} /> DM admin terhubung{adminTg.title ? ` (${adminTg.title})` : ""} · Chat ID: {adminTg.chat_id}</span>
                ) : (
                  "Belum terhubung. DM admin akan menerima notifikasi setiap ada konfirmasi transfer baru."
                )}
              </p>
            </div>
          </div>

          <div className="bg-[var(--surface-base)] rounded-lg p-4 border border-[var(--border-subtle)] mb-5">
            <p className="text-sm font-semibold mb-2">Cara menghubungkan DM pribadi admin:</p>
            <ol className="text-sm text-[var(--text-secondary)] space-y-1 list-decimal list-inside">
              <li>Dari akun Telegram admin, buka chat pribadi ke bot <span className="text-accent font-medium">@{status?.bot_username || "FasTradingInvestama_bot"}</span>.</li>
              <li>Kirim pesan <b>/start</b> atau pesan apa saja ke bot tersebut.</li>
              <li>Klik <b>Hubungkan Admin Telegram</b> di bawah ini.</li>
            </ol>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => act(() => api.post("/telegram/admin/detect").then(loadAdmin), "admin-detect", "DM admin berhasil terhubung!")}
              disabled={busy === "admin-detect"}
              data-testid="tg-admin-detect-btn"
              className="h-10 px-4 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors"
            >
              {busy === "admin-detect" ? "Mendeteksi..." : "Hubungkan Admin Telegram"}
            </button>
            <button
              onClick={() => act(() => api.post("/telegram/admin/test"), "admin-test", "Pesan tes terkirim ke DM admin!")}
              disabled={busy === "admin-test" || !adminTg?.configured}
              data-testid="tg-admin-test-btn"
              className="h-10 px-4 rounded-lg bg-[var(--surface-hover)] border border-[var(--border)] font-semibold hover:bg-[var(--border)] disabled:opacity-40 transition-colors"
            >
              {busy === "admin-test" ? "Mengirim..." : "Kirim Tes ke DM Admin"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
